/**
 * ROROS Layout Configuration
 *
 * This file registers the ROROS sidebar navigation with the boilerplate's
 * layout registry. Importing this module as a side effect is sufficient —
 * `registerLayoutConfig('roros', rorosLayoutConfig)` is called at module
 * load time, before any React rendering.
 *
 * Rule #0 compliance: the boilerplate's layoutConfigs.tsx contains no
 * ROROS-specific entries. All ROROS nav lives here.
 */

import React from 'react';
import {
  LayoutDashboard,
  Radio,
  ClipboardList,
  Network,
  Target,
  CheckCircle2,
  BarChart3,
  RefreshCw,
  Rocket,
  BookOpen,
  Lightbulb,
  GitBranch,
  ShieldCheck,
  Library,
  Play,
  // Landmark + FlaskConical removed: TB-1 (AC-DM-08) deleted the Governance nav section.
  Activity,
  Building,
  Settings,
  CalendarClock,
  Calendar,
  ClipboardCheck,
  Bot,
  ScrollText,
  Wallet,
  ShieldCheck as ShieldCheckIcon,
  MessagesSquare,
  Inbox,
  // AC-OPSNAV-1: Scale icon for Decisions — visually distinct from Strategic Tree (GitBranch).
  Scale,
} from 'lucide-react';
import { registerLayoutConfig } from '@/components/Layout/layoutConfigs';
import type { LayoutConfig } from '@/components/Layout/layoutConfigs';
import { PERMISSIONS } from '@/constants/permissions';
// W2-G: wrap nav labels with t() so the extractor picks them up and they
// translate at render time (lazy functions). NavItem.label now accepts
// string | (() => string) — see BaseLayout.tsx.
import { t } from '@/lib/i18n';

export const rorosLayoutConfig: LayoutConfig = {
  sidebarTitle: 'Rocket Routine OS',
  headerSubtitle: 'Rocket Routine OS',
  rootPath: '/roros',
  navSections: [
    {
      items: [
        { label: () => t('Dashboard'), href: '/roros', icon: React.createElement(LayoutDashboard, { className: 'w-5 h-5' }) },
      ],
    },
    {
      title: 'Command',
      items: [
        { label: () => t('Control Tower'), href: '/roros/control-tower', icon: React.createElement(Radio, { className: 'w-5 h-5' }) },
        // 'Work Items' → /roros/work-items removed: it pointed to a "Coming Soon"
        // placeholder; work items are visible inside Control Tower (real surface).
        // AC-inbox-14: unified Inbox replaces Verification Inbox in Command
        { label: () => t('Inbox'), href: '/roros/inbox', icon: React.createElement(Inbox, { className: 'w-5 h-5' }) },
        // Ticket 206334ed (2026-06-04): Consultations moved here from the Actors
        // section, placed between Inbox (formerly "Verification Inbox") and
        // Strategic Tree per Sven's board request.
        { label: () => t('Consultations'), href: '/roros/consultations', icon: React.createElement(MessagesSquare, { className: 'w-5 h-5' }) },
        // AC-CHAT-5/6: nav label changed to "Chat"; navigates directly to /new (one-click fresh session).
        // Deep-link /roros/agent-sessions still renders the list (see AppRouter.tsx).
        { label: () => t('Chat'), href: '/roros/agent-sessions/new', icon: React.createElement(Bot, { className: 'w-5 h-5' }) },
        // AC-OPSNAV-1: Decisions moved here from the (removed) Operations section.
        // Placed immediately after Consultations and immediately before Strategic Tree.
        // Icon changed from GitBranch to Scale — visually distinct from Strategic Tree.
        { label: () => t('Decisions'), href: '/roros/decisions', icon: React.createElement(Scale, { className: 'w-5 h-5' }) },
        // Strategic Tree (Goal Wave B) — moved here from Artifacts per Sven
        // dogfood 2026-05-02. The tree is a command-center visualisation of
        // the Vision → Goal → Outcome hierarchy, not an artifact CRUD page.
        { label: () => t('Strategic Tree'), href: '/roros/artifacts/tree', icon: React.createElement(GitBranch, { className: 'w-5 h-5' }) },
      ],
    },
    {
      // M-Rhythm-1: "Cadence" section renamed to "Rhythm", moved to position 2
      // (immediately after Command). Calendar replaces Schedules; path updated.
      // Old: /roros/cadence (Schedules) — redirects to /roros/rhythm/calendar for 2 weeks.
      title: 'Rhythm',
      items: [
        {
          // Wave 4C: Single "Calendar" entry for the unified Calendar + List hub.
          // The separate "Meetings" nav entry is removed (AC-4C-5).
          label: () => t('Calendar'),
          href: '/roros/rhythm/calendar',
          icon: React.createElement(Calendar, { className: 'w-5 h-5' }),
        },
        {
          label: () => t('Weekly Review'),
          // M-Rhythm-1: path moved from /roros/cadence/weekly-review
          // Old path has a redirect alias registered in the router.
          href: '/roros/rhythm/weekly-review',
          icon: React.createElement(ClipboardCheck, { className: 'w-5 h-5' }),
        },
        // AC-inbox-14/16: Cascade Inbox nav entry removed; folded into unified /roros/inbox.
      ],
    },
    {
      // Wave H (AC-NAV-1): "AI" group renamed "Actors". Items: Actors · Role Contracts
      // · Delegations · Consultations. "AI Operators" nav entry replaced by "Actors"
      // → /roros/actors. Skills retained as a 5th item (separate F-SKILL-1 feature).
      title: 'Actors',
      items: [
        // AC-NAV-1: single "Actors" entry replaces "AI Operators"
        { label: () => t('Actors'), href: '/roros/actors', icon: React.createElement(Bot, { className: 'w-5 h-5' }) },
        // J7-W2 (PRD §12.3.10 task 2) — Role Contracts full editor.
        { label: () => t('Role Contracts'), href: '/roros/role-contracts', icon: React.createElement(ScrollText, { className: 'w-5 h-5' }) },
        // Ticket f54ccb84 (2026-06-04): Skills moved up to sit directly below
        // Role Contracts (was previously the last item in this section).
        // F-SKILL-1 Packet B1 — Skills library (AC-SKILL-01). Visible to users with VIEW perm.
        {
          label: () => t('Skills'),
          href: '/roros/skills',
          icon: React.createElement(Library, { className: 'w-5 h-5' }),
          requiredPermissions: [PERMISSIONS.ROROS_AGENT_SKILLS_VIEW],
        },
        // J7-W3 (PRD §12.3.10 task 3) — Delegations.
        // Ticket 206334ed (2026-06-04): Consultations moved to the Command section
        // (between Inbox and Strategic Tree).
        { label: () => t('Delegations'), href: '/roros/delegations', icon: React.createElement(ShieldCheckIcon, { className: 'w-5 h-5' }) },
      ],
    },
    // Structure section removed — its nav entries (Domains, Actors, AI Operators,
    // Role Contracts) are consolidated into the "Structure Settings" tabbed page
    // under the Configuration group. AI Operators was already a redirect to
    // /roros/actors?type=ai_operator and is dropped from the sidebar entirely.
    // See docs/plans/archived/2026-04-15-roros-configuration-consolidation.md
    {
      title: 'Artifacts',
      items: [
        { label: () => t('Goals'), href: '/roros/artifacts/goals', icon: React.createElement(Target, { className: 'w-5 h-5' }) },
        // AC-FW3a-4-1: Annual Outcomes merged into OutcomesPage (horizon filter).
        // The separate /roros/outcomes/annual route redirects here for back-compat.
        { label: () => t('Outcomes'), href: '/roros/artifacts/outcomes', icon: React.createElement(CheckCircle2, { className: 'w-5 h-5' }) },
        // Strategic Tree moved to the Command section (above) per Sven
        // dogfood 2026-05-02 — it's a command-center visualisation, not a
        // CRUD page. OMPRIKL canonical order resumes from Metrics on.
        { label: () => t('Metrics'), href: '/roros/artifacts/metrics', icon: React.createElement(BarChart3, { className: 'w-5 h-5' }) },
        // Ticket 6941e519 (2026-06-04): Principles nav entry + standalone page
        // removed — principles are managed in the Constitution tab of Structure
        // Settings. Old /roros/artifacts/principles URLs redirect there (router).
        { label: () => t('Routines'), href: '/roros/artifacts/routines', icon: React.createElement(RefreshCw, { className: 'w-5 h-5' }) },
        { label: () => t('Initiatives'), href: '/roros/artifacts/initiatives', icon: React.createElement(Rocket, { className: 'w-5 h-5' }) },
        { label: () => t('Knowledge'), href: '/roros/artifacts/knowledge', icon: React.createElement(BookOpen, { className: 'w-5 h-5' }) },
        { label: () => t('Learning'), href: '/roros/artifacts/learning', icon: React.createElement(Lightbulb, { className: 'w-5 h-5' }) },
      ],
    },
    // AC-OPSNAV-4: Operations section removed entirely.
    // - Decisions → moved to the Command section above (AC-OPSNAV-1).
    // - Intake Sources → moved to the last tab in Structure Settings (AC-OPSNAV-2).
    // - No other items were in Operations; no menu entry is lost.
    //
    // TB-1 (AC-DM-08 / AC-DM-10): Governance sidebar section removed entirely.
    // - "Governance" item → replaced by "Decision Making" tab in Structure Settings.
    // - "Exploration Zones" item → hidden for v1; routes remain registered (dormant).
    // Routes /roros/governance, /roros/governance/exploration-zones, /roros/governance/exploration-zones/:id
    // remain in AppRouter — only the nav entries are removed here.
    // M-Rhythm-1: Old "Cadence" section removed; "Rhythm" section (above) replaces it.
    // /roros/cadence → 301 redirect to /roros/rhythm/calendar (in router).
    // /roros/cadence/weekly-review → redirect to /roros/rhythm/weekly-review.
    {
      title: 'Insights',
      items: [
        { label: () => t('Observability'), href: '/roros/observability', icon: React.createElement(Activity, { className: 'w-5 h-5' }) },
        // Ticket bcea6efb (2026-06-04): the "Road Dashboard" nav entry is removed.
        // The top-level "Dashboard" (/roros → RorosHomeRoute) renders the same
        // RoadDashboardPage, so this Insights entry was a duplicate. The
        // /roros/dashboards/road route stays for back-compat; only the sidebar
        // entry is dropped.
        // Cadence Scorecard is gated to Growth/Scaling modes — RorosSidebarLayout
        // strips it in Founder mode.
        { label: () => t('Cadence Scorecard'), href: '/roros/dashboards/scorecard', icon: React.createElement(BarChart3, { className: 'w-5 h-5' }) },
        // J7-W2 (PRD §12.3.10 task 5) — Budget Ledger viewer.
        { label: () => t('Budget Ledger'), href: '/roros/governance/budget-ledger', icon: React.createElement(Wallet, { className: 'w-5 h-5' }) },
        // Company Profile moved to the Configuration section below.
      ],
    },
    {
      title: 'Configuration',
      items: [
        {
          label: () => t('Company Settings'),
          href: '/roros/company-profile',
          icon: React.createElement(Building, { className: 'w-5 h-5' }),
          // Visible to everyone with `roros.overarching.view` (Employees get
          // this permission too — the page renders in read-only mode for
          // users without `roros.overarching.manage`).
          requiredPermissions: [PERMISSIONS.ROROS_OVERARCHING_VIEW],
        },
        {
          label: () => t('Structure Settings'),
          href: '/roros/structure-settings',
          icon: React.createElement(Network, { className: 'w-5 h-5' }),
          // TB-1 (AC-SSV-05): Structure Settings visible to ALL ROROS users.
          // Previously gated to ROROS_STRUCTURE_MANAGE (Owner/Admin only).
          // Tabs inside the page self-gate editing on their own manage permissions.
          // No requiredPermissions: all authenticated ROROS users see this entry.
        },
        {
          label: () => t('System Settings'),
          href: '/roros/settings',
          icon: React.createElement(Settings, { className: 'w-5 h-5' }),
          // Visible if the user has ANY of these — System Settings contains
          // tabs gated by org settings, roles, and user management perms.
          // The page itself hides individual tabs per permission.
          requiredPermissions: [
            PERMISSIONS.ROROS_SETTINGS_MANAGE,
            PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS,
            PERMISSIONS.MANAGE_ROLES,
            PERMISSIONS.VIEW_USERS,
            PERMISSIONS.MANAGE_USERS,
          ],
        },
      ],
    },
  ],
};

// Register with the boilerplate layout registry — top-level side effect.
// Importing this module is sufficient; no explicit call is needed at the call site.
registerLayoutConfig('roros', rorosLayoutConfig);
